// Tile surface shader chunks + the extruded hex prism geometry — split out
// of TerrainTiles.tsx (which keeps the instancing component; per-biome
// colour helpers live in tilePaint.ts).

import * as THREE from 'three';
import {
  FLAT_RELIEF,
  TILE_PLATEAU,
  TILE_RADIUS,
  TILE_RELIEF,
  hexNoise,
  smoothField,
} from './elevation.js';
import { ROCK_DARK, ROCK_LIGHT, SAND_DRY } from './shore/shoreColors.js';
import { GLSL_UNPACK_RGB, packRGB } from './terrainEdges.js';

/** Prism height below the top: tall enough to reach under the deepest water. */
export const TILE_COLUMN_DEPTH = 1.5;
const BEVEL = 0.03;
/**
 * Wall slope — the base ring flares OUTWARD (wider than the top), so a
 * raised tile reads as a diagonal slope against its lower neighbours instead
 * of a vertical cylinder wall. 45° from horizontal: the skirt runs
 * TILE_COLUMN_DEPTH outward over its 1.5 depth. For a gentler 30° slope use
 * TILE_COLUMN_DEPTH * Math.tan(Math.PI / 6) ≈ 0.87 of run instead.
 */

const WALL_FLARE = (TILE_RADIUS + TILE_COLUMN_DEPTH) / TILE_RADIUS;

export const CRAG_AMP: Record<string, readonly [number, number]> = {
  // [vertical jag, radial push]
  mountain: [0.13, 0.12],
  hills: [0.06, 0.06],
  desert: [0.03, 0.04],
};

export const FLAT_CRAG: readonly [number, number] = [0.025, 0.03];

const SUN = new THREE.Vector3(-0.45, 0.8, 0.4).normalize();

const SHORE_COLOR: Record<string, string> = {
  mountain: '#a39d8c', // shingle
  tundra: '#d4d6c8', // pale pebbles
  default: SAND_DRY, // sand
};

/** Apothem of the flat plateau: where the chamfer starts, measured from the centre. */
const PLATEAU_APOTHEM = TILE_PLATEAU * Math.cos(Math.PI / 6);
/** Width of the neighbour-colour blend band: the outer ~15% of the plateau. */
const BLEND_BAND = PLATEAU_APOTHEM * 0.15;
/** Share of the neighbour's colour at the plateau edge (0.5 would meet exactly halfway). */
const BLEND_STRENGTH = 0.42;

export const glslFloat = (v: number) => v.toFixed(5);

/** Shared edge-river flow time, advanced by RiverAnimator (HexGrid). */
export const riverTime = { value: 0 };
const glslVec3 = (c: THREE.Color) =>
  `vec3(${glslFloat(c.r)}, ${glslFloat(c.g)}, ${glslFloat(c.b)})`;

/**
 * Vertex + fragment additions for the per-edge coast and biome-blend
 * shading (see terrainEdges.ts). Fragment code runs right after
 * <color_fragment> and replaces diffuseColor with the edited tile colour
 * times the baked shade, so the noise and snow passes still apply on top.
 */
export function tileEdgeShader(biome: string) {
  const shore = glslVec3(new THREE.Color(SHORE_COLOR[biome] ?? SHORE_COLOR.default));
  const vertexPars = /* glsl */ `
    attribute vec4 aEdgeMask;
    attribute vec3 aNbA;
    attribute vec3 aNbB;
    attribute vec3 aTone;
    // Per-tile flag texture (tileData.ts, P7.13): aTone.z carries the tile's
    // global index, sampled here so the packed-flag room in the attribute is
    // no longer the feature ceiling.
    uniform sampler2D uTileData;
    uniform vec2 uTileDataSize;
    varying vec3 _vLocal;
    varying vec3 _vTileCol;
    varying float _vShade;
    varying vec2 _vTone;
    flat varying ivec4 _vMask;
    flat varying int _vRiverEdge;
    varying vec3 _vNb0; varying vec3 _vNb1; varying vec3 _vNb2;
    varying vec3 _vNb3; varying vec3 _vNb4; varying vec3 _vNb5;
    ${GLSL_UNPACK_RGB}`;
  const vertexMain = /* glsl */ `
    _vLocal = position;
    _vShade = color.r;
    #ifdef USE_INSTANCING_COLOR
      _vTileCol = instanceColor.rgb;
    #else
      _vTileCol = vec3(1.0);
    #endif
    _vTone = aTone.xy;
    _vMask = ivec4(aEdgeMask + 0.5);
    // Legacy layout, new source: R | G << 6 === the old riv + code * 64
    // (wall code 4 << 6 = 256 = bit 8; volcano 1..3 = bits 6..7). texelFetch
    // ignores filtering, so Nearest + no mipmaps is belt and braces.
    int _tdId = int(aTone.z + 0.5);
    ivec2 _tdUV = ivec2(_tdId % int(uTileDataSize.x), _tdId / int(uTileDataSize.x));
    vec4 _td = texelFetch(uTileData, _tdUV, 0);
    _vRiverEdge = int(_td.r * 255.0 + 0.5) | (int(_td.g * 255.0 + 0.5) << 6);
    _vNb0 = _unpackRGB(aNbA.x); _vNb1 = _unpackRGB(aNbA.y); _vNb2 = _unpackRGB(aNbA.z);
    _vNb3 = _unpackRGB(aNbB.x); _vNb4 = _unpackRGB(aNbB.y); _vNb5 = _unpackRGB(aNbB.z);`;
  const fragmentPars = /* glsl */ `
    uniform float uRiverTime;
    varying vec3 _vLocal;
    varying vec3 _vTileCol;
    varying float _vShade;
    varying vec2 _vTone;
    flat varying ivec4 _vMask;
    flat varying int _vRiverEdge;
    varying vec3 _vNb0; varying vec3 _vNb1; varying vec3 _vNb2;
    varying vec3 _vNb3; varying vec3 _vNb4; varying vec3 _vNb5;
    vec3 _fogTone(vec3 c) {
      float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
      return mix(vec3(l), c, _vTone.y) * _vTone.x;
    }`;
  const fragmentMain = /* glsl */ `
    {
      vec3 _base = _vTileCol;
      // Sea mask in the low six bits, its cliff (rock) subset above them (terrainEdges.buildTileEdgeAttributes).
      int _seaM = _vMask.x & 63;
      int _cliffM = (_vMask.x >> 6) & 63;
      int _beachM = _seaM & (~_cliffM);
      // Top and chamfer only: the side walls keep the plain tile colour (but see the cliff faces below).
      if (_vLocal.y > ${glslFloat(-BEVEL - 0.002)} && (_seaM | _vMask.w) != 0) {
        vec3 _nb[6] = vec3[6](_vNb0, _vNb1, _vNb2, _vNb3, _vNb4, _vNb5);
        vec3 _cSum = vec3(0.0);
        float _wSum = 0.0;
        float _sand = 0.0;
        float _wet = 0.0;
        float _rock = 0.0;
        for (int k = 0; k < 6; k++) {
          float _a = radians(60.0 + 60.0 * float(k));
          vec2 _n = vec2(cos(_a), sin(_a));
          // Distance in from this edge's plateau line (negative on the chamfer).
          float _d = ${glslFloat(PLATEAU_APOTHEM)} - dot(_vLocal.xz, _n);
          int _bit = 1 << k;
          if ((_vMask.w & _bit) != 0) {
            // Ragged, not ruler-straight: the band edge wanders a little.
            float _rag = sin(_vWorldPos.x * 5.3 + _vWorldPos.z * 3.1) * sin(_vWorldPos.z * 6.7 - _vWorldPos.x * 2.3);
            float _w = 1.0 - smoothstep(0.0, ${glslFloat(BLEND_BAND)}, _d + 0.035 * _rag);
            _wSum += _w;
            _cSum += _w * _nb[k];
          }
          float _along = dot(_vWorldPos.xz, vec2(-_n.y, _n.x));
          if ((_beachM & _bit) != 0) {
            // A wide, wavy beach band, continuous in world space: sand at the edge fading softly into the
            // land colour (the ramp mesh carries the same sand on down into the water).
            float _wob = 0.03 * sin(_along * 7.3 + _vWorldPos.x * 1.7) + 0.02 * sin(_along * 17.0 + _vWorldPos.z * 3.1);
            _sand = max(_sand, 1.0 - smoothstep(0.0, 0.24, _d + _wob));
            _wet = max(_wet, 1.0 - smoothstep(-0.03, 0.03, _d + _wob * 0.5));
          }
          if ((_cliffM & _bit) != 0) {
            // A darker, rocky lip along the top of a cliff.
            float _wob = 0.02 * sin(_along * 11.0 + _vWorldPos.x * 2.7);
            _rock = max(_rock, 1.0 - smoothstep(0.0, 0.1, _d + _wob));
          }
        }
        if (_wSum > 0.0) {
          _base = mix(_base, _fogTone(_cSum / _wSum), ${glslFloat(BLEND_STRENGTH)} * min(_wSum, 1.0));
        }
        vec3 _shore = _fogTone(${shore});
        _shore = mix(_shore, _shore * vec3(0.78, 0.8, 0.84), _wet);
        _base = mix(_base, _shore, _sand * 0.95);
        _base = mix(_base, _fogTone(${glslVec3(new THREE.Color(ROCK_LIGHT))}) * 0.82, _rock * 0.7);

        // Edge rivers: a water channel hugging each river edge, with damp
        // banks and a gentle flow. Each tile draws its own half, so the
        // channel reads as continuous across the border.
        int _rv = _vRiverEdge & 63; // high bits carry the volcano code (volcanoTileShader.ts)
        if (_rv != 0) {
          for (int k = 0; k < 6; k++) {
            int _rbit = 1 << k;
            if ((_rv & _rbit) == 0) continue;
            float _ra = radians(60.0 + 60.0 * float(k));
            vec2 _rn = vec2(cos(_ra), sin(_ra));
            vec2 _rt = vec2(-_rn.y, _rn.x);
            float _dEdge = ${glslFloat(PLATEAU_APOTHEM)} - dot(_vLocal.xz, _rn);
            if (_dEdge < -0.005) continue; // past the plateau, on the chamfer
            float _along = dot(_vWorldPos.xz, _rt);
            float _meander = 0.035 * sin(_along * 3.4 + uRiverTime * 0.35)
                           + 0.02 * sin(_along * 7.7 - uRiverTime * 0.6);
            float _halfW = 0.10 + 0.035 * sin(_along * 2.2 + 1.7);
            float _bankD = abs(_dEdge + _meander);
            float _water = 1.0 - smoothstep(0.045, _halfW, _bankD);
            float _bank = (1.0 - smoothstep(_halfW, _halfW + 0.06, _bankD)) * (1.0 - _water);
            // Dark damp earth fading out from the channel.
            _base = mix(_base, _base * vec3(0.66, 0.62, 0.54), _bank * 0.8);
            // Water: deep teal with a moving glint.
            vec3 _riverCol = _fogTone(vec3(0.16, 0.35, 0.45));
            float _flow = 0.5 + 0.5 * sin(_along * 9.0 - uRiverTime * 2.2);
            _riverCol += vec3(0.06, 0.09, 0.1) * _flow;
            _base = mix(_base, _riverCol, _water * 0.92);
          }
        }
      } else if (_cliffM != 0 && _vLocal.y <= ${glslFloat(-BEVEL - 0.002)}) {
        // Cliff face: the sea-facing wall becomes banded rock, wet and dark toward the waterline.
        float _best = -1e3;
        int _bk = 0;
        for (int k = 0; k < 6; k++) {
          float _a = radians(60.0 + 60.0 * float(k));
          float _dp = dot(_vLocal.xz, vec2(cos(_a), sin(_a)));
          if (_dp > _best) { _best = _dp; _bk = k; }
        }
        if ((_cliffM & (1 << _bk)) != 0) {
          float _strata = 0.5 + 0.5 * sin(_vWorldY * 38.0 + _vWorldPos.x * 3.1 + _vWorldPos.z * 2.3
                                         + 1.5 * sin(_vWorldPos.x * 7.0 + _vWorldPos.z * 5.0));
          float _fleck = fract(sin(dot(floor(vec2(_vWorldPos.x + _vWorldPos.z, _vWorldY * 9.0) * 6.0), vec2(12.9, 78.2))) * 43758.5);
          vec3 _rk = mix(${glslVec3(new THREE.Color(ROCK_DARK))}, ${glslVec3(new THREE.Color(ROCK_LIGHT))}, 0.32 + 0.38 * _strata + 0.2 * _fleck);
          float _wetRock = 1.0 - smoothstep(-0.32, 0.06, _vWorldY);
          _rk *= 1.0 - 0.35 * _wetRock;
          _base = _fogTone(_rk);
        }
      }
      diffuseColor.rgb = _base * _vShade;
    }`;
  return { vertexPars, vertexMain, fragmentPars, fragmentMain };
}

/**
 * Baked Lambert shade for a face normal, normalised so a flat top is 1.0.
 * Walls facing the sun land around 0.8, walls facing away around 0.5.
 */
export function bakedShade(nx: number, ny: number, nz: number): number {
  const lambert = Math.max(0, nx * SUN.x + ny * SUN.y + nz * SUN.z);
  return Math.min(1.08, 0.5 + (0.5 * lambert) / SUN.y);
}

function cornerXZ(i: number, radius: number): [number, number] {
  const a = ((30 + 60 * (i % 6)) * Math.PI) / 180;
  return [Math.cos(a) * radius, Math.sin(a) * radius];
}

/**
 * A hex prism built by hand, with its plateau at y = 0 and its body reaching
 * down TILE_COLUMN_DEPTH, so each instance is placed at its surface height.
 *
 * The top has three raised rings — centre, peak (new: craggy sub-ring at
 * r≈0.24) and mid (r=0.5) — with per-corner baked noise, then the plateau
 * ring and a chamfer to the rim at TILE_RADIUS (< 1, leaving a thin gap so
 * every tile shows its sides). Heights come from TILE_RELIEF, the same table
 * reliefAt() reads.
 *
 * Below the chamfer the wall *flares outward* (base ring wider than the rim,
 * 45° slope — WALL_FLARE), so a raised tile reads as a diagonal hillside
 * against its lower neighbours rather than a vertical cookie-cutter cliff.
 *
 * Because one geometry is shared by every tile of a biome, per-tile
 * asymmetry comes from the vertex shader: each vertex carries `aRing`
 * (0 centre / 1 peak / 2 mid / 3 static) + `aCorner` (0..5), and instances
 * carry `aSeed` (set per tile from q,r) that the shader hashes into corner
 * jags and radial pushes — mountains come out craggy and unique instead of
 * rotationally symmetric tents. Flat-shaded, with the baked sun shading in
 * vertex colours.
 */
export function makeTileGeometry(noiseSeed: number, biome: string): THREE.BufferGeometry {
  const [centreLift, midLift, jag] = TILE_RELIEF[biome] ?? FLAT_RELIEF;
  const noise = (i: number) => Math.sin(i * 2.39 + noiseSeed * 11.3) * 0.5 + 0.5; // 0..1
  const peakRadius = 0.24;

  // Unique-vertex staging. The final buffers hold emitted triangles only —
  // leaking the staged verts into the position attribute would give
  // computeVertexNormals 31 bogus "faces" (the original code avoided this by
  // taking coordinate triples directly).
  const staging: number[] = [];
  const stagingRing: number[] = [];
  const stagingCorner: number[] = [];
  const verts: number[] = [];
  const rings: number[] = [];
  const corners: number[] = [];
  let v = 0;
  /** Push one vertex, tracking its ring + corner for the shader. */
  const vert = (x: number, y: number, z: number, ringId: number, cornerId: number): number => {
    staging.push(x, y, z);
    stagingRing.push(ringId);
    stagingCorner.push(cornerId);
    return v++;
  };
  const centreV = vert(0, centreLift + (noise(7) - 0.5) * jag, 0, 0, 0);
  const peak = Array.from({ length: 6 }, (_, i) => {
    const [x, z] = cornerXZ(i, peakRadius);
    const h = centreLift * 0.55 + midLift * 0.45 + (noise(i + 11) - 0.5) * jag * 1.4;
    return vert(x, h, z, 1, i);
  });
  const mid = Array.from({ length: 6 }, (_, i) => {
    const [x, z] = cornerXZ(i, 0.5);
    return vert(x, midLift + (noise(i) - 0.5) * jag * 2, z, 2, i);
  });
  const plateau = Array.from({ length: 6 }, (_, i) => {
    const [x, z] = cornerXZ(i, TILE_PLATEAU);
    return vert(x, 0, z, 3, i);
  });
  const rim = Array.from({ length: 6 }, (_, i) => {
    const [x, z] = cornerXZ(i, TILE_RADIUS);
    return vert(x, -BEVEL, z, 3, i);
  });
  // Wall flares outward: the base is wider than the rim so steps between
  // tiles slope diagonally (see WALL_FLARE).
  const base = Array.from({ length: 6 }, (_, i) => {
    const [x, z] = cornerXZ(i, TILE_RADIUS * WALL_FLARE);
    return vert(x, -TILE_COLUMN_DEPTH, z, 3, i);
  });

  const cross = new THREE.Vector3();
  const e1 = new THREE.Vector3();
  const e2 = new THREE.Vector3();
  /** Push a triangle (by vertex index), flipped so it faces `outward`. */
  const tri = (a: number, b: number, c: number, outward: [number, number, number]) => {
    const ax = staging[a * 3]!;
    const ay = staging[a * 3 + 1]!;
    const az = staging[a * 3 + 2]!;
    const bx = staging[b * 3]!;
    const by = staging[b * 3 + 1]!;
    const bz = staging[b * 3 + 2]!;
    const cx = staging[c * 3]!;
    const cy = staging[c * 3 + 1]!;
    const cz = staging[c * 3 + 2]!;
    e1.set(bx - ax, by - ay, bz - az);
    e2.set(cx - ax, cy - ay, cz - az);
    cross.crossVectors(e1, e2);
    const facing = cross.x * outward[0] + cross.y * outward[1] + cross.z * outward[2];
    const order = facing >= 0 ? [a, b, c] : [a, c, b];
    for (const idx of order) {
      rings.push(stagingRing[idx]!);
      corners.push(stagingCorner[idx]!);
      verts.push(staging[idx * 3]!, staging[idx * 3 + 1]!, staging[idx * 3 + 2]!);
    }
  };
  const UP: [number, number, number] = [0, 1, 0];
  for (let i = 0; i < 6; i++) {
    const j = (i + 1) % 6;
    tri(centreV, peak[i]!, peak[j]!, UP);
    tri(peak[i]!, mid[i]!, mid[j]!, UP);
    tri(peak[i]!, mid[j]!, peak[j]!, UP);
    tri(mid[i]!, plateau[i]!, plateau[j]!, UP);
    tri(mid[i]!, plateau[j]!, mid[j]!, UP);
    // Chamfer + wall face outward, along the edge's mid-normal.
    const [ox, oz] = cornerXZ(i + 0.5, 1);
    const out: [number, number, number] = [ox, 0, oz];
    const outUp: [number, number, number] = [ox, 1, oz];
    tri(plateau[i]!, rim[i]!, rim[j]!, outUp);
    tri(plateau[i]!, rim[j]!, plateau[j]!, outUp);
    tri(rim[i]!, base[i]!, base[j]!, out);
    tri(rim[i]!, base[j]!, rim[j]!, out);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
  // One vec2 instead of two float attributes — the tile program sits at the
  // driver's vertex-attribute limit (instancing + edge channels), so every
  // attribute slot counts.
  const ringCorner = new Float32Array((verts.length / 3) * 2);
  for (let i = 0; i < verts.length / 3; i++) {
    ringCorner[i * 2] = rings[i]!;
    ringCorner[i * 2 + 1] = corners[i]!;
  }
  geo.setAttribute('aRingCorner', new THREE.Float32BufferAttribute(ringCorner, 2));
  geo.computeVertexNormals(); // non-indexed => per-face normals (flat shading)

  const positions = geo.getAttribute('position') as THREE.BufferAttribute;
  const normals = geo.getAttribute('normal') as THREE.BufferAttribute;
  const colors = new Float32Array(positions.count * 3);
  for (let i = 0; i < positions.count; i++) {
    let shade = bakedShade(normals.getX(i), normals.getY(i), normals.getZ(i));
    // Darken the walls toward the bottom so tall columns read as depth.
    const y = positions.getY(i);
    if (y < -BEVEL) shade *= 0.72 + 0.28 * Math.max(0, 1 + y / 0.6);
    colors[i * 3] = shade;
    colors[i * 3 + 1] = shade;
    colors[i * 3 + 2] = shade;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}
